/**
 * One list of everything only an admin can reach.
 *
 * Both things read from here: the quiet lock markers in the UI (via
 * `<AdminOnlyMark id="…" />`) and the Admin only page. A control cannot be
 * admin-gated in the code and missing from the page, or listed on the page and
 * unmarked on screen, because there is one list.
 *
 * `kind` is what the thing IS, because "this whole page is mine" and "this one
 * button is mine" are different facts:
 *   page      — a whole screen
 *   section   — a block inside a screen other roles can open
 *   operation — a button or action, usually destructive or spend-related
 */
export const ADMIN_SURFACES_RECONCILED = '2026-09-29';

export const ADMIN_SURFACES = [
  // ── whole screens ───────────────────────────────────────────────────────
  { id: 'owner-dashboard', kind: 'page', label: 'Owner Dashboard', where: 'Left nav',
    path: '/app/owner-dashboard',
    what: 'How the business is doing, and the risks that need attention today.' },
  { id: 'control-centre', kind: 'page', label: 'Control Centre', where: 'Left nav',
    path: '/app/control-centre',
    what: 'Every app setting in one place, with who changed it and what it changes.' },
  { id: 'settings', kind: 'page', label: 'Settings', where: 'Admin block',
    path: '/app/settings',
    what: 'ATS defaults, organisation details, enrichment and approved email senders.' },
  { id: 'integration-keys', kind: 'page', label: 'Integration Keys', where: 'Admin block',
    path: '/app/admin/security',
    what: 'The provider API keys the whole app sources and enriches with.' },
  { id: 'scoring-engine', kind: 'page', label: 'Scoring Engine', where: 'Admin block',
    path: '/app/scoring-engine',
    what: 'How candidates are scored, what a change would do, and the re-score runs.' },
  { id: 'marketing-analytics', kind: 'page', label: 'Marketing & SEO', where: 'Admin block',
    path: '/app/marketing-analytics',
    what: 'Public website traffic, careers-funnel conversion and SEO health.' },
  { id: 'app-usage', kind: 'page', label: 'App Usage', where: 'Admin block',
    path: '/app/product-analytics',
    what: 'Which screens are used, which are never opened, and who has never logged in.' },
  { id: 'admin-tools', kind: 'page', label: 'Admin Tools', where: 'Admin block',
    path: '/app/admin-tools',
    what: 'Security events, system pressure and the operational tooling.' },
  { id: 'compliance-hub', kind: 'page', label: 'Compliance Hub', where: 'Admin block',
    path: '/app/admin',
    what: 'One entry to every privacy, retention and data-handling screen.' },
  { id: 'privacy-settings', kind: 'page', label: 'Privacy Settings', where: 'Admin block',
    path: '/app/admin/compliance',
    what: 'Retention windows, consent text and the data-handling policy.' },
  { id: 'dsar-queue', kind: 'page', label: 'DSAR Queue', where: 'Admin block',
    path: '/app/admin/dsar',
    what: 'Statutory data requests and their 30-day clock.' },
  { id: 'data-health', kind: 'page', label: 'Data Health', where: 'Admin block',
    path: '/app/admin/data-health',
    what: 'Unparsed resumes, placeholder names and unranked candidates, with fix-now buttons.' },
  { id: 'data-hygiene', kind: 'page', label: 'Data Hygiene', where: 'Admin block',
    path: '/app/admin/data-hygiene',
    what: 'The repair and migration tools, including the destructive ones.' },
  { id: 'salary-repair', kind: 'page', label: 'Salary Repair', where: 'Admin block',
    path: '/app/admin/salary-repair',
    what: 'Re-reads badly parsed salary figures across the candidate pool.' },
  { id: 'share-grants', kind: 'page', label: 'Share Admin Access', where: 'Admin block',
    path: '/app/admin/share-grants',
    what: 'Lends admin powers to somebody else, with an expiry and a paper trail.' },
  { id: 'email-routing', kind: 'page', label: 'Email routing', where: 'Compliance Hub → Email',
    path: '/app/admin/email-routing',
    what: 'Which email streams leave through Resend, and each mailbox’s reserve.' },
  { id: 'billing', kind: 'page', label: 'Billing', where: 'Direct link',
    path: '/app/billing',
    what: 'Plan, invoices and what the app is spending.' },
  { id: 'api-usage', kind: 'page', label: 'API Usage', where: 'Admin block',
    path: '/app/admin/api-usage',
    what: 'Provider credit burn, and the switches that turn a paid source off.' },
  { id: 'admin-only', kind: 'page', label: 'Admin only', where: 'Admin block',
    path: '/app/admin/admin-only',
    what: 'This page — every surface that stops at your own login.' },

  // ── blocks inside screens other roles can open ──────────────────────────
  { id: 'email-delivery', kind: 'section', label: 'Email Delivery',
    where: 'Admin → Email routing', path: '/app/admin/email-routing',
    what: 'Sets the Resend key, the From address, and proves it with a test send.' },
  { id: 'departures-sweep', kind: 'operation', label: 'Handover sweep',
    where: 'People → header', path: '/app/team',
    what: 'Hands over everything still held by people who have already left, sets their mail backups, and reports anything nobody could be chosen for.' },
  { id: 'mail-switchover', kind: 'operation', label: 'Moving automated mail to Resend',
    where: 'Admin → Email routing', path: '/app/admin/email-routing',
    what: 'Switches transactional mail to Resend, runs the three-day bulk ramp, sets the daily and hourly send caps, pauses a single mail type, and returns everything to Gmail.' },
  { id: 'delivery-truth', kind: 'section', label: 'Where the mail actually left from',
    where: 'Admin → Email routing', path: '/app/admin/email-routing',
    what: 'The recorded provider and reason behind every send of the last 30 days, with failures and held-back sends grouped by cause.' },
  { id: 'sourcing-sources', kind: 'section', label: 'Sources (sourcing results)',
    where: 'Talent Hub → Sourcing → footer', path: '/app/candidates?tab=sourcing',
    what: 'Per-provider depth, quota and cost, and how much of the result set came free from the search index.' },
  { id: 'sourcing-index-wording', kind: 'section', label: 'Search-index markers',
    where: 'Talent Hub → Sourcing → rows', path: '/app/candidates?tab=sourcing',
    what: 'The “Search index” badge, the “In index” chip and the data-freshness stamp on a row.' },
  { id: 'segment-tagging', kind: 'section', label: 'Segment tagging',
    where: 'Scoring Engine → Data health', path: '/app/scoring-engine',
    what: 'Role-family coverage and where each tag came from.' },

  // ── operations ──────────────────────────────────────────────────────────
  { id: 'rescore-all-jobs', kind: 'operation', label: 'Re-score every job',
    where: 'Scoring Engine', path: '/app/scoring-engine',
    what: 'Re-scores the whole database. The AI phase spends credits and needs an explicit confirm.' },
  { id: 'scoring-config-save', kind: 'operation', label: 'Save or restore scoring weights',
    where: 'Scoring Engine', path: '/app/scoring-engine',
    what: 'Changes how every candidate is scored; each save is kept with a field-level diff.' },
  { id: 'destructive-migrations', kind: 'operation', label: 'Destructive migrations',
    where: 'Data Hygiene', path: '/app/admin/data-hygiene',
    what: 'Rewrites or removes data. Unlocks only after you type APPLY.' },
  { id: 'review-admins', kind: 'operation', label: 'Review admins / bulk role change',
    where: 'People', path: '/app/people',
    what: 'Promotes or demotes accounts in bulk. You can never demote yourself or the last admin.' },
  { id: 'normalize-roles', kind: 'operation', label: 'Normalise stored roles',
    where: 'Data Hygiene', path: '/app/admin/data-hygiene',
    what: 'Cleans a role stored as “Manager” to its canonical value, dry-run first.' },
  { id: 'rotate-jwt', kind: 'operation', label: 'Rotate the session secret',
    where: 'Admin Tools → Security', path: '/app/admin-tools?tab=security',
    what: 'Signs everybody out of every device immediately.' },
  { id: 'machine-mail-switch', kind: 'operation', label: 'Move all machine mail to the company address',
    where: 'Control Centre → Communication', path: '/app/control-centre?q=resend',
    what: 'Switches every automated stream from personal Gmail to the branded address.' },
  { id: 'admin-highlight', kind: 'operation', label: 'Show what’s admin-only',
    where: 'Your profile menu', path: '',
    what: 'Outlines every admin-exclusive control on the screen you are looking at.' },
];

export const adminSurface = (id) => ADMIN_SURFACES.find((s) => s.id === id) || null;
export const adminSurfaceCount = ADMIN_SURFACES.length;
